import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import './EditDeviceModal.css';

const EditDeviceModal = ({ device, onClose, onSave }) => {
  const [name, setName] = useState(device?.DeviceName || '');
  const [allUsers, setAllUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState(device?.Users || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`/users`);
        const users = (res.data.users || []).map(({ user }) => ({
          UserId: user.UserId,
          Name: user.Name,
          Phone: user.Phone
        }));
        setAllUsers(users);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }
    const q = searchTerm.trim().toLowerCase();
    setSuggestions(
      allUsers.filter(u => {
        const matches =
          u.Name.toLowerCase().includes(q) ||
          u.Phone.includes(q);
        const already = assignedUsers.some(a => a.UserId === u.UserId);
        return matches && !already;
      })
    );
  }, [searchTerm, allUsers, assignedUsers]);

  const handleAddUser = user => {
    setAssignedUsers(prev => [...prev, user]);
    setSearchTerm('');
    setSuggestions([]);
  };

  const handleRemoveUser = user => {
    setAssignedUsers(prev =>
      prev.filter(u => u.UserId !== user.UserId)
    );
    if (editingUser?.UserId === user.UserId) {
      setEditingUser(null);
    }
  };

  const getUserAccessType = (user) => {
    // First check the IsDelegate flag from backend
    if (!user.IsDelegate) {
      return 'permanent';
    }
    
    // Only check time-based access for delegates
    if (user.StartDate && user.EndDate) {
      const now = new Date();
      const startDate = new Date(user.StartDate);
      const endDate = new Date(user.EndDate);
      
      if (endDate < now) return 'expired';
      if (startDate > now) return 'pending';
      return 'temporary';
    }
    
    // If IsDelegate is true but no dates, treat as permanent (shouldn't happen)
    return 'permanent';
  };

  const handleEditTimeAccess = (user) => {
    setEditingUser(user);
    setEditStartDate(user.StartDate ? new Date(user.StartDate).toISOString().slice(0, 16) : '');
    setEditEndDate(user.EndDate ? new Date(user.EndDate).toISOString().slice(0, 16) : '');
  };

  const handleSaveTimeEdit = () => {
    if (!editStartDate || !editEndDate) {
      alert('Both start and end dates are required');
      return;
    }
    if (new Date(editEndDate) <= new Date(editStartDate)) {
      alert('End date must be after start date');
      return;
    }

    setAssignedUsers(prev => prev.map(user => 
      user.UserId === editingUser.UserId 
        ? { ...user, StartDate: editStartDate, EndDate: editEndDate, IsDelegate: true }
        : user
    ));
    setEditingUser(null);
  };

  const handleCancelTimeEdit = () => {
    setEditingUser(null);
    setEditStartDate('');
    setEditEndDate('');
  };

  const handleConvertToPermanent = (user) => {
    setAssignedUsers(prev => prev.map(u => 
      u.UserId === user.UserId 
        ? { ...u, StartDate: null, EndDate: null, IsDelegate: false }
        : u
    ));
  };

  const handleSetExpiration = (user) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    // Set default dates and enter edit mode
    setEditStartDate(tomorrow.toISOString().slice(0, 16));
    setEditEndDate(nextWeek.toISOString().slice(0, 16));
    setEditingUser(user);
  };

  const handleSave = () => {
    onSave(device, name.trim(), assignedUsers);
    onClose();
  };

  return (
    <div className="edit-device-backdrop">
      <div className="edit-device-modal">
        <h2>Edit Device</h2>

        <div className="edit-device-input-group">
          <label className="edit-device-label">Name</label>
          <input
            className="edit-device-input"
            type="text"
            placeholder="Enter device name"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        <div className="edit-device-input-group">
          <label className="edit-device-label">
            Assign Users (optional)
          </label>
          <input
            className="edit-device-input"
            type="text"
            placeholder="Search by name or phone…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          {suggestions.length > 0 && (
            <ul className="edit-device-suggestions">
              {suggestions.map(u => (
                <li
                  key={u.UserId}
                  className="edit-device-suggestion-item"
                  onClick={() => handleAddUser(u)}
                  data-testid={`user-suggestion-${u.Name}`}
                >
                  {u.Name} ({u.Phone})
                </li>
              ))}
            </ul>
          )}

          {assignedUsers.length > 0 && (
            <div className="assigned-users">
              {assignedUsers.map(u => {
                const accessType = getUserAccessType(u);
                const isEditing = editingUser?.UserId === u.UserId;
                
                return (
                  <div key={u.UserId} className={`user-container ${accessType}`}>
                    {!isEditing ? (
                      <span 
                        className={`user-tag ${accessType}-access`}
                        onClick={() => accessType !== 'permanent' && handleEditTimeAccess(u)}
                        style={{ cursor: accessType !== 'permanent' ? 'pointer' : 'default' }}
                      >
                        <span className="access-icon">
                          {accessType === 'permanent' && 'PERM'}
                          {accessType === 'temporary' && 'TEMP'}
                          {accessType === 'pending' && 'PEND'}
                          {accessType === 'expired' && 'EXP'}
                        </span>
                        {u.Name} ({u.Phone})
                        {(accessType === 'temporary' || accessType === 'pending' || accessType === 'expired') && u.StartDate && u.EndDate && (
                          <span className="date-range">
                            • {new Date(u.StartDate).toLocaleDateString()} - {new Date(u.EndDate).toLocaleDateString()}
                          </span>
                        )}
                        {accessType === 'expired' && <span className="status-badge expired">EXPIRED</span>}
                        {accessType === 'pending' && <span className="status-badge pending">PENDING</span>}
                        <button
                          type="button"
                          className="remove-user-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveUser(u);
                          }}
                          data-testid={`remove-user-${u.UserId}`}
                        >
                          ×
                        </button>
                      </span>
                    ) : (
                      <div className="inline-edit-container">
                        <div className="edit-user-info">
                          <span className="access-icon">TEMP</span>
                          {u.Name} ({u.Phone})
                        </div>
                        <div className="date-edit-inputs">
                          <input
                            type="datetime-local"
                            value={editStartDate}
                            onChange={(e) => setEditStartDate(e.target.value)}
                            className="date-edit-input"
                            min={new Date().toISOString().slice(0, 16)}
                          />
                          <span className="date-separator">to</span>
                          <input
                            type="datetime-local"
                            value={editEndDate}
                            onChange={(e) => setEditEndDate(e.target.value)}
                            className="date-edit-input"
                            min={editStartDate || new Date().toISOString().slice(0, 16)}
                          />
                        </div>
                        <div className="edit-actions">
                          <button
                            type="button"
                            className="save-edit-button"
                            onClick={handleSaveTimeEdit}
                          >
                            SAVE
                          </button>
                          <button
                            type="button"
                            className="cancel-edit-button"
                            onClick={handleCancelTimeEdit}
                          >
                            CANCEL
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {!isEditing && (
                      <div className="user-actions">
                        {accessType === 'permanent' && (
                          <button
                            type="button"
                            className="action-button set-expiration"
                            onClick={() => handleSetExpiration(u)}
                          >
                            Set Expiration
                          </button>
                        )}
                        {(accessType === 'temporary' || accessType === 'pending' || accessType === 'expired') && (
                          <button
                            type="button"
                            className="action-button convert-permanent"
                            onClick={() => handleConvertToPermanent(u)}
                          >
                            Make Permanent
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="edit-device-actions">
          <button
            onClick={onClose}
            className="edit-device-button cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="edit-device-button save"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDeviceModal;